import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useLevels, activeLevelNames } from '@/hooks/useLevels';

export default function Login() {
  const { user, profile, profileLoading, isTeacher, signIn, signUpStudent } = useAuth();
  const { levels } = useLevels();
  const levelOptions = activeLevelNames(levels);
  const navigate = useNavigate();

  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [level, setLevel] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && profile && !profileLoading) {
      navigate(isTeacher ? '/panel' : '/ogrenci', { replace: true });
    }
  }, [user, profile, profileLoading, isTeacher, navigate]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      const { data, error } = await signIn(email.trim(), password);
      if (error) setError(error);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bilinmeyen bir hata oluştu.');
    }
    setSubmitting(false);
  };

  const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Şifreler eşleşmiyor.');
      return;
    }
    if (!level) {
      setError('Lütfen seviyenizi seçin.');
      return;
    }
    setSubmitting(true);
    try {
      const { error, needsEmailConfirm } = await signUpStudent({
        fullName: fullName.trim(),
        email: email.trim(),
        password,
        phone: phone.trim(),
        level,
      });
      if (error) {
        setError(error);
        return;
      }
      if (needsEmailConfirm) {
        setInfo('Kaydınız oluşturuldu. E-postanıza gönderilen onay linkine tıklayarak hesabınızı aktifleştirin.');
        setMode('login');
        setPassword('');
        setConfirmPassword('');
      }
    } catch (err) {
      console.error('Signup hatası:', err);
      setError(err instanceof Error ? err.message : 'Kayıt sırasında beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background-50">
      {/* Sol marka paneli */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-gradient-to-br from-primary-950 via-primary-900 to-primary-800 p-12 lg:flex">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-accent-500/10 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-primary-500/20 blur-3xl" />

        <div className="relative flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-500 font-heading text-2xl font-extrabold text-primary-950">
            Z
          </div>
          <div className="leading-tight">
            <p className="font-heading text-lg font-bold text-white">Zehra Hocayla</p>
            <p className="text-sm font-medium text-accent-300">Matematik</p>
          </div>
        </div>

        <div className="relative">
          <span className="inline-block rounded-full bg-accent-500/15 px-4 py-1.5 text-sm font-semibold text-accent-300 ring-1 ring-accent-500/30">
            13 Yıllık Tecrübe · Soru Yazarlığı
          </span>
          <h1 className="mt-5 font-heading text-4xl font-bold leading-tight text-white xl:text-5xl">
            Matematiği ezberletmeden,<br />anlaşılır hale getiriyoruz.
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-primary-200">
            Ders programını takip et, ders kayıtlarına ulaş, dokümanları indir ve ödevlerini tek panelden yönet.
          </p>

          <div className="mt-10 grid max-w-md grid-cols-3 gap-4">
            {[
              { value: '13+', label: 'Yıl Tecrübe' },
              { value: '150+', label: 'Aktif Öğrenci' },
              { value: '500+', label: 'Ders Kaydı' },
            ].map((s) => (
              <div key={s.label} className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
                <p className="font-heading text-2xl font-bold text-accent-400">{s.value}</p>
                <p className="mt-1 text-xs text-primary-200">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-sm text-primary-300">
          <i className="ri-instagram-line mr-1.5" />
          @mathocaaofficall
        </p>
      </div>

      {/* Sağ form paneli */}
      <div className="flex w-full items-center justify-center px-4 py-10 lg:w-1/2 lg:px-12">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500 font-heading text-2xl font-extrabold text-white">
              Z
            </div>
            <div className="leading-tight">
              <p className="font-heading text-lg font-bold text-foreground-950">Zehra Hocayla</p>
              <p className="text-sm font-medium text-accent-600">Matematik</p>
            </div>
          </div>

          <h2 className="font-heading text-2xl font-bold text-foreground-950">
            {mode === 'login' ? 'Tekrar hoş geldin' : 'Hesap oluştur'}
          </h2>
          <p className="mt-1.5 text-sm text-foreground-600">
            {mode === 'login'
              ? 'Hesabına giriş yaparak panele ulaş.'
              : 'Birkaç adımda öğrenci hesabını oluştur.'}
          </p>

          {/* Giriş / Kayıt seçimi */}
          <div className="mt-6 grid grid-cols-2 gap-1 rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => {
                setMode('login');
                setError(null);
                setInfo(null);
              }}
              className={`rounded-full px-4 py-2.5 text-sm font-semibold transition cursor-pointer whitespace-nowrap ${
                mode === 'login' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              Giriş Yap
            </button>
            <button
              type="button"
              onClick={() => {
                setMode('signup');
                setError(null);
                setInfo(null);
              }}
              className={`rounded-full px-4 py-2.5 text-sm font-semibold transition cursor-pointer whitespace-nowrap ${
                mode === 'signup' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              Kayıt Ol
            </button>
          </div>

          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground-800">E-posta</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
                    <i className="ri-mail-line text-base" />
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="ornek@eposta.com"
                    className="w-full rounded-lg border border-background-300 bg-white py-2.5 pl-10 pr-3.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Şifre</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
                    <i className="ri-lock-2-line text-base" />
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="w-full rounded-lg border border-background-300 bg-white py-2.5 pl-10 pr-10 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 transition hover:text-foreground-700 cursor-pointer"
                    aria-label="Şifreyi göster/gizle"
                  >
                    <i className={showPassword ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'} />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-600">
                  <input type="checkbox" className="h-4 w-4 rounded accent-accent-500" />
                  Beni hatırla
                </label>
                <button type="button" className="text-sm font-semibold text-primary-600 transition hover:text-primary-700 cursor-pointer whitespace-nowrap">
                  Şifremi unuttum
                </button>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-primary-500 py-3 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
              >
                {submitting ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSignup} className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ad Soyad</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  placeholder="Örn: Elif Kaya"
                  className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground-800">E-posta</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="ornek@eposta.com"
                  className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Telefon</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="05xx xxx xx xx"
                    className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Seviye</label>
                  <select
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
                  >
                    <option value="" disabled>Seviye seçin</option>
                    {levelOptions.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Şifre</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="En az 6 karakter"
                  className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Şifre (Tekrar)</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Şifreni tekrar gir"
                  className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-primary-500 py-3 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
              >
                {submitting ? 'Hesap oluşturuluyor...' : 'Hesap Oluştur'}
              </button>
            </form>
          )}

          {error && (
            <p className="mt-5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-200">
              <i className="ri-error-warning-line mr-1.5" />
              {error}
            </p>
          )}

          {info && (
            <p className="mt-5 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700 ring-1 ring-emerald-200">
              <i className="ri-information-line mr-1.5" />
              {info}
            </p>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-background-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-background-50 px-3 text-xs font-medium text-foreground-500">veya</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate('/misafir')}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-background-300 bg-white py-3 text-sm font-semibold text-foreground-800 transition hover:bg-background-50 cursor-pointer"
            >
              <i className="ri-user-heart-line text-base" />
              Misafir olarak dene (deneme dersi)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}